import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from "@angular/router";
import { Store } from "@ngrx/store";
import {
	AuthenticationStatus,
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	KycState,
	RequiredActions,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";

import { CustomerVerificationConfigService } from "./customer-verification.config";
import { VerificationType } from "./customer-verification.model";
import { CustomerVerificationService } from "./customer-verification.service";

@Injectable({
	providedIn: "root",
})
export class CustomerVerificationGuard implements CanActivate {
	constructor(
		private store: Store<CoreState>,
		private service: CustomerVerificationService,
		private telemetryService: TelemetryService,
		private config: CustomerVerificationConfigService,
		private deviceConfigService: DeviceConfigService
	) {}

	canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
		return this.store.select(AuthSelector.getAuthenticationStatus).pipe(
			filter((status) => status !== AuthenticationStatus.Pending),
			take(1),
			switchMap((status) => {
				if (
					(route.queryParams && route.queryParams.variant === VerificationType.verified) ||
					status === AuthenticationStatus.NotAuthenticated
				) {
					return of(true);
				}

				if (route.queryParams && route.queryParams.variant) {
					if (!this.config.isNoWelcomePageAfterLoginEnabled()) {
						return of(true);
					}

					this.telemetryService.track({
						event: "optimize.activate",
					});

					const isDesktop = this.deviceConfigService.isDesktop();

					if (!isDesktop && route.queryParams.variant === VerificationType.withRewards) {
						this.store.dispatch(RouteAction.navigate(CORE_ROUTE.reward.welcomeBonus));
					} else {
						return this.checkDepositEligibility().pipe(
							map((canDeposit) => {
								if (canDeposit) {
									this.store.dispatch(
										RouteAction.navigate(
											CORE_ROUTE.payment.deposit,
											undefined,
											!isDesktop
												? {
														queryParams: {
															redirect: CORE_ROUTE.home,
															accountCreated: "true",
														},
													}
												: undefined
										)
									);
								}
								return false;
							})
						);
					}

					return of(false);
				}

				this.service.initialize();

				return this.service.getAvailableRewards$().pipe(
					map((registerRewardsPayload) => {
						this.store.dispatch(
							RouteAction.navigate(CORE_ROUTE.registration.registerSuccess, undefined, {
								queryParams: {
									variant: registerRewardsPayload.verificationType,
								},
							})
						);
						return false;
					})
				);
			})
		);
	}

	private checkDepositEligibility(): Observable<boolean> {
		return this.store.select(JurisdictionSelector.get).pipe(
			take(1),
			switchMap((jurisdiction) => {
				if (jurisdiction !== Jurisdiction.BRA) {
					return of(true);
				}
				return combineLatest([
					this.store.select(CustomerManagementSelector.getAllowedAction),
					this.store.select(CustomerManagementSelector.getKYCStatus),
					this.store.select(CustomerManagementSelector.getRequiredActions),
				]).pipe(
					take(1),
					map(([allowedActions, kycStatus, requiredActions]) => {
						const isDepositAllowed = !!allowedActions?.deposit?.isAllowed;
						const isDocumentUploadRequired = requiredActions?.some(
							(r) => r.actionType === RequiredActions.DocumentUpload
						);
						const isDocumentProcessing = kycStatus === KycState.None && !isDocumentUploadRequired;
						return isDepositAllowed && !isDocumentProcessing;
					})
				);
			})
		);
	}
}
